import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { metricVisibilityApi } from '../services/api';
import { HubThemeScope } from './clients/hub/designSystem';

interface MetricVisibility {
  metric_id: number;
  metric_name: string;
  hidden: boolean;
}

interface MetricVisibilityDialogProps {
  open: boolean;
  onClose: () => void;
  dashboardId?: number | null;
  departmentId?: number | null;
  isMixed?: boolean;
  onSaved?: () => void;
}

const MetricVisibilityDialog: React.FC<MetricVisibilityDialogProps> = ({
  open,
  onClose,
  dashboardId,
  departmentId,
  isMixed,
  onSaved,
}) => {
  const [metrics, setMetrics] = useState<MetricVisibility[]>([]);
  const [filteredMetrics, setFilteredMetrics] = useState<MetricVisibility[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      loadVisibility();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on open/ids change only
  }, [open, dashboardId, departmentId, isMixed]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredMetrics(metrics);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredMetrics(
        metrics.filter((m) => m.metric_name.toLowerCase().includes(term))
      );
    }
  }, [searchTerm, metrics]);

  const loadVisibility = async () => {
    try {
      setLoading(true);
      const data = await metricVisibilityApi.get({
        dashboardId: dashboardId ?? null,
        departmentId: departmentId ?? null,
        isMixed: isMixed ?? false,
      });
      // Deduplicate by metric_id (keep first occurrence)
      const seen = new Set<number>();
      const unique = data.filter((m) => {
        if (seen.has(m.metric_id)) {
          return false;
        }
        seen.add(m.metric_id);
        return true;
      });
      setMetrics(unique);
    } catch (error) {
      console.error('Failed to load metric visibility:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (metricId: number) => {
    setMetrics((prev) =>
      prev.map((m) =>
        m.metric_id === metricId ? { ...m, hidden: !m.hidden } : m
      )
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Deduplicate by metricId before saving
      const seen = new Set<number>();
      const uniqueVisibility = metrics
        .filter((m) => {
          if (seen.has(m.metric_id)) {
            return false;
          }
          seen.add(m.metric_id);
          return true;
        })
        .map((m) => ({
          metricId: m.metric_id,
          hidden: m.hidden,
        }));
      
      await metricVisibilityApi.batchUpdate(uniqueVisibility, {
        dashboardId: dashboardId ?? null,
        departmentId: departmentId ?? null,
        isMixed: isMixed ?? false,
      });
      
      // Clean up duplicates after saving
      try {
        await metricVisibilityApi.cleanup();
      } catch (cleanupError) {
        console.warn('Cleanup failed (non-critical):', cleanupError);
      }
      
      onSaved?.();
      onClose();
    } catch (error) {
      console.error('Failed to save visibility:', error);
      alert('Failed to save visibility settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectAll = () => {
    const allVisible = metrics.every((m) => !m.hidden);
    // If all are visible, hide them all. Otherwise, show them all.
    const newHiddenValue = allVisible;
    setMetrics((prev) =>
      prev.map((m) => ({ ...m, hidden: newHiddenValue }))
    );
  };

  const visibleCount = metrics.filter((m) => !m.hidden).length;
  const hiddenCount = metrics.filter((m) => m.hidden).length;

  return (
    <HubThemeScope>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Show/Hide Metrics</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {visibleCount} visible, {hiddenCount} hidden
            </Typography>
            <Button size="small" onClick={handleSelectAll}>
              {metrics.every((m) => !m.hidden) ? 'Hide All' : 'Show All'}
            </Button>
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Search metrics..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Box
            sx={{
              maxHeight: 400,
              overflowY: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
            }}
          >
            {loading ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                Loading metrics...
              </Typography>
            ) : filteredMetrics.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No metrics found
              </Typography>
            ) : (
              filteredMetrics.map((metric) => (
                <FormControlLabel
                  key={metric.metric_id}
                  control={
                    <Checkbox
                      checked={!metric.hidden}
                      onChange={() => handleToggle(metric.metric_id)}
                    />
                  }
                  label={metric.metric_name}
                  sx={{ display: 'flex', width: '100%', mb: 0.5 }}
                />
              ))
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
    </HubThemeScope>
  );
};

export default MetricVisibilityDialog;
